import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiArrowRight,
  FiBookOpen,
  FiTarget,
  FiEye,
  FiCheck,
  FiX,
  FiHome,
} from 'react-icons/fi';
import '../styles/LessonStyles.css';
import GetStartedWith from '../../../../components/GetStartedWith';

const questionTypes = [
  {
    id: 1,
    type: 'Main Idea Questions',
    description:
      'Ask about the primary purpose, main point, or central theme of the passage.',
    frequency: 'Very Common',
    keywords: [
      'main purpose',
      'primary purpose',
      'central theme',
      'main point',
      'primarily concerned with',
    ],
    strategy:
      'Focus on the thesis statement and overall argument structure. Ask yourself: "What is the author trying to convince me of?"',
    examples: [
      'The primary purpose of the passage is to...',
      'The author is primarily concerned with...',
      'Which of the following best describes the main point of the passage?',
    ],
    tips: [
      'Look for repeated themes and concepts',
      'Pay attention to the conclusion paragraph',
      'Eliminate answers that are too specific or too broad',
      'The main idea should encompass the entire passage',
    ],
  },
  {
    id: 2,
    type: 'Detail Questions',
    description:
      'Ask about specific information explicitly stated in the passage.',
    frequency: 'Very Common',
    keywords: [
      'according to the passage',
      'the passage states',
      'mentioned in the passage',
      'the author indicates',
    ],
    strategy:
      'Return to the passage and find the exact location where the information is discussed. Look for direct textual support.',
    examples: [
      'According to the passage, which of the following is true about...?',
      'The passage indicates that...',
      'The author mentions which of the following as an example of...?',
    ],
    tips: [
      'Use keywords from the question to locate relevant passage sections',
      'Look for synonyms and paraphrases of the answer in the passage',
      'Avoid answers that require inference or assumption',
      'The correct answer will be directly supported by the text',
    ],
  },
  {
    id: 3,
    type: 'Inference Questions',
    description:
      'Ask what can be logically concluded from the passage without being explicitly stated.',
    frequency: 'Common',
    keywords: ['implies', 'suggests', 'inferred', 'most likely', 'probably'],
    strategy:
      'Look for logical connections between ideas. What must be true based on what is stated?',
    examples: [
      'The passage suggests that...',
      'It can be inferred from the passage that...',
      'The author implies that...',
    ],
    tips: [
      'Stay close to the passage - avoid wild leaps of logic',
      'Look for cause-and-effect relationships',
      "Consider what the author assumes but doesn't explicitly state",
      'Eliminate answers that contradict the passage',
    ],
  },
  {
    id: 4,
    type: "Author's Tone/Attitude",
    description:
      "Ask about the author's attitude, tone, or perspective toward the subject.",
    frequency: 'Common',
    keywords: [
      "author's attitude",
      'tone',
      "author's view",
      'author regards',
      "author's perspective",
    ],
    strategy:
      'Look at word choice, examples used, and overall presentation. Is the author critical, supportive, neutral, or enthusiastic?',
    examples: [
      "The author's attitude toward the theory can best be described as...",
      'The tone of the passage is...',
      'The author regards the phenomenon with...',
    ],
    tips: [
      'Pay attention to adjectives and descriptive language',
      'Look for qualifying words (however, although, despite)',
      'Consider both positive and negative language used',
      'Avoid extreme tone words unless clearly supported',
    ],
  },
  {
    id: 5,
    type: 'Function Questions',
    description:
      'Ask about the role or purpose of a specific part of the passage.',
    frequency: 'Common',
    keywords: ['function', 'purpose', 'role', 'in order to', 'serves to'],
    strategy:
      'Understand how each part contributes to the overall argument or structure of the passage.',
    examples: [
      'The author mentions X in order to...',
      'The function of the second paragraph is to...',
      'The author discusses Y primarily to...',
    ],
    tips: [
      'Consider the context before and after the referenced text',
      'Think about how the part supports the main argument',
      'Look for transition words that indicate purpose',
      'Consider whether it provides evidence, contrast, or elaboration',
    ],
  },
  {
    id: 6,
    type: 'Strengthen/Weaken Questions',
    description:
      'Ask which statement would strengthen or weaken an argument in the passage.',
    frequency: 'Less Common',
    keywords: [
      'strengthen',
      'weaken',
      'support',
      'undermine',
      'most consistent with',
    ],
    strategy:
      'Identify the argument structure first, then determine what additional evidence would help or hurt the case.',
    examples: [
      "Which of the following would most strengthen the author's argument?",
      "The author's claim would be weakened by evidence that...",
      'Which statement is most consistent with the passage?',
    ],
    tips: [
      'Clearly identify the specific argument being referenced',
      'For strengthen questions, look for additional supporting evidence',
      'For weaken questions, look for contradictory evidence',
      'Stay within the logical framework of the passage',
    ],
  },
  {
    id: 7,
    type: 'Select-in-Passage Questions',
    description:
      'Ask you to click on a sentence in the passage that performs a specific function.',
    frequency: 'Occasional',
    keywords: [
      'select the sentence',
      'click on the sentence',
      'highlight the sentence',
    ],
    strategy:
      "Read the question carefully to understand exactly what function you're looking for, then scan the passage systematically.",
    examples: [
      "Select the sentence that provides evidence for the author's main claim.",
      'Click on the sentence that introduces a contrasting viewpoint.',
      'Highlight the sentence that best summarizes the passage.',
    ],
    tips: [
      'Read the question multiple times to understand the precise requirement',
      'Consider the function within the context of surrounding sentences',
      'Look for sentences with strong supporting evidence or clear transitions',
      "Ensure the selected sentence directly addresses the question's requirement",
    ],
  },
  {
    id: 8,
    type: 'Multiple Choice Select All',
    description:
      'Ask you to select all correct answers from a list of options.',
    frequency: 'Less Common',
    keywords: [
      'select all that apply',
      'choose all correct answers',
      'indicate all',
    ],
    strategy:
      'Evaluate each option independently - treat it as multiple true/false questions rather than choosing just one.',
    examples: [
      'Which of the following are mentioned in the passage? Select all that apply.',
      'The passage suggests which of the following about the theory? Choose all correct answers.',
      'According to the passage, the researchers found evidence of which phenomena? Indicate all that apply.',
    ],
    tips: [
      "Don't assume there's a specific number of correct answers",
      'Each option must be independently verifiable from the passage',
      "Be more conservative - only select answers you're confident about",
      'Return to the passage for each option to verify',
    ],
  },
];

const practiceQuestions = [
  {
    id: 1,
    type: 'Main Idea',
    passage:
      'The traditional view of coral reefs as static, unchanging ecosystems has been fundamentally challenged by recent research. Scientists now understand that coral reefs are highly dynamic systems that constantly adapt to environmental changes through various mechanisms. These adaptations include shifts in coral species composition, changes in symbiotic relationships with algae, and modifications in reef structure. This new understanding has significant implications for conservation efforts, suggesting that protecting coral reefs requires strategies that account for their inherent flexibility and adaptive capacity rather than attempting to preserve them in a fixed state.',
    question: 'The primary purpose of the passage is to:',
    options: [
      'Describe the various mechanisms by which coral reefs adapt to environmental changes',
      'Present new research findings that change our understanding of coral reef ecosystems',
      'Argue that traditional conservation methods for coral reefs are completely ineffective',
      'Explain why coral reefs are more vulnerable to environmental damage than previously thought',
    ],
    correct: 1,
    explanation:
      "The passage's main purpose is to present how recent research has changed our understanding of coral reefs from static to dynamic systems. While it mentions adaptation mechanisms, the primary focus is on the shift in scientific understanding and its implications.",
    questionTypeExplanation:
      'This is a Main Idea question asking for the primary purpose. Look for the overarching theme that encompasses the entire passage, not just specific details mentioned within it.',
  },
  {
    id: 2,
    type: 'Detail',
    passage:
      'Photosynthesis in plants involves two main stages: the light-dependent reactions and the Calvin cycle. The light-dependent reactions occur in the thylakoid membranes and produce ATP and NADPH using energy from sunlight. The Calvin cycle takes place in the stroma and uses the ATP and NADPH to convert carbon dioxide into glucose. Chlorophyll, the primary photosynthetic pigment, absorbs light most efficiently in the blue and red wavelengths while reflecting green light, which is why plants appear green to our eyes.',
    question: 'According to the passage, the Calvin cycle:',
    options: [
      'Occurs in the thylakoid membranes and produces ATP',
      'Takes place in the stroma and converts CO2 into glucose',
      'Absorbs light most efficiently in blue and red wavelengths',
      'Reflects green light, making plants appear green',
    ],
    correct: 1,
    explanation:
      'The passage explicitly states that "The Calvin cycle takes place in the stroma and uses the ATP and NADPH to convert carbon dioxide into glucose."',
    questionTypeExplanation:
      'This is a Detail question asking for information directly stated in the passage. The correct answer can be found word-for-word or closely paraphrased in the text.',
  },
  {
    id: 3,
    type: 'Inference',
    passage:
      'The ancient city of Pompeii provides archaeologists with an unprecedented glimpse into daily life in the Roman Empire. Unlike other archaeological sites where only fragments of artifacts remain, Pompeii was preserved virtually intact by volcanic ash from Mount Vesuvius in 79 CE. The sudden burial meant that everyday objects, from bread still in ovens to political graffiti on walls, remained exactly as they were at the moment of the disaster. This preservation has allowed researchers to study not just the elite classes, as is typical with most Roman sites, but also the lives of ordinary citizens, slaves, and merchants.',
    question: 'It can be inferred from the passage that:',
    options: [
      'Most Roman archaeological sites provide information primarily about wealthy Romans',
      'Mount Vesuvius erupted multiple times during the Roman Empire',
      'Pompeii was the largest city in the Roman Empire',
      'Archaeological preservation is always better in volcanic environments',
    ],
    correct: 0,
    explanation:
      'The passage states that Pompeii allows study of "not just the elite classes, as is typical with most Roman sites," implying that other Roman sites mainly provide information about the wealthy.',
    questionTypeExplanation:
      'This is an Inference question requiring you to draw a logical conclusion from the given information. The answer is not explicitly stated but can be reasonably deduced from the text.',
  },
];

export default function QuestionTypesLesson() {
  const navigate = useNavigate();
  const practiceRef = useRef(null);
  const [selectedType, setSelectedType] = useState(null);
  const [practiceAnswers, setPracticeAnswers] = useState({});
  const [showExplanations, setShowExplanations] = useState({});

  const scrollToPractice = () => {
    practiceRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAnswerSelect = (questionId, answerIndex) => {
    setPracticeAnswers((prev) => ({
      ...prev,
      [questionId]: answerIndex,
    }));
  };

  const toggleExplanation = (questionId) => {
    setShowExplanations((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

  const renderOverview = () => (
    <div className="lesson-content">
      <h2 className="section-title">Master the 8 Types of RC Questions</h2>

      <div className="lesson-intro">
        <p>
          Every GRE Reading Comprehension question falls into one of eight
          distinct categories. Learning to quickly identify question types will
          help you apply the right strategy and avoid common traps. Each type
          requires a specific approach and has predictable patterns.
        </p>
      </div>

      <div className="question-types-grid">
        {questionTypes.map((type) => (
          <div
            key={type.id}
            onClick={() => setSelectedType(type)}
            className={`question-type-card ${selectedType?.id === type.id ? 'selected' : ''}`}
          >
            <div className="type-header">
              <div className="type-number">{type.id}</div>
              <h3 className="type-title">{type.type}</h3>
            </div>
            <p className="type-description">{type.description}</p>
            <div className="frequency-indicator">
              <span
                className={`frequency ${type.frequency.toLowerCase().replace(' ', '-')}`}
              >
                {type.frequency}
              </span>
            </div>
          </div>
        ))}
      </div>

      {selectedType && (
        <div className="detailed-analysis">
          <h3 className="analysis-title">
            <FiTarget />
            {selectedType.type}
          </h3>

          <div className="analysis-section">
            <h4>Recognition Keywords:</h4>
            <div className="keywords-container">
              {selectedType.keywords.map((keyword, index) => (
                <span key={index} className="keyword-tag">
                  "{keyword}"
                </span>
              ))}
            </div>
          </div>

          <div className="analysis-section">
            <h4>Strategy:</h4>
            <div className="strategy-box">{selectedType.strategy}</div>
          </div>

          <div className="analysis-section">
            <h4>Example Question Stems:</h4>
            <ul className="example-list">
              {selectedType.examples.map((example, index) => (
                <li key={index} className="example-item">
                  "{example}"
                </li>
              ))}
            </ul>
          </div>

          <div className="analysis-section">
            <h4 className="tips-header">Success Tips:</h4>
            <ul className="tips-list">
              {selectedType.tips.map((tip, index) => (
                <li key={index}>{tip}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );

  const renderPractice = () => (
    <div ref={practiceRef} className="practice-section">
      <h2 className="section-title">Practice: Identify Question Types</h2>

      {practiceQuestions.map((question) => (
        <div key={question.id} className="practice-passage">
          <h3 className="passage-title">
            Practice Question {question.id} - {question.type} Question
          </h3>

          <div className="passage-text">
            {question.passage.split('. ').map((sentence, index) => (
              <span key={index}>
                {sentence}
                {index < question.passage.split('. ').length - 1 ? '. ' : ''}
              </span>
            ))}
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <p
              style={{
                color: 'var(--liwaza-on-surface)',
                fontWeight: 'bold',
                marginBottom: '1rem',
              }}
            >
              {question.question}
            </p>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
              }}
            >
              {question.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(question.id, index)}
                  style={{
                    background:
                      practiceAnswers[question.id] === index
                        ? 'var(--liwaza-primary-container)'
                        : 'var(--liwaza-surface-container-highest)',
                    color:
                      practiceAnswers[question.id] === index
                        ? 'var(--liwaza-on-primary-container)'
                        : 'var(--liwaza-on-surface)',
                    border:
                      practiceAnswers[question.id] === index
                        ? '2px solid var(--liwaza-primary)'
                        : '1px solid var(--liwaza-outline-variant)',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  {String.fromCharCode(65 + index)}. {option}
                </button>
              ))}
            </div>

            {practiceAnswers[question.id] !== undefined && (
              <button
                onClick={() => toggleExplanation(question.id)}
                className="show-explanation-btn"
                style={{ marginTop: '1rem' }}
              >
                {showExplanations[question.id]
                  ? 'Hide Explanation'
                  : 'Show Explanation'}
              </button>
            )}

            {showExplanations[question.id] && (
              <div className="explanation-box">
                <div style={{ marginBottom: '1rem' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                    }}
                  >
                    {practiceAnswers[question.id] === question.correct ? (
                      <FiCheck style={{ color: 'var(--liwaza-success)' }} />
                    ) : (
                      <FiX style={{ color: 'var(--liwaza-error)' }} />
                    )}
                    <h5
                      style={{
                        color:
                          practiceAnswers[question.id] === question.correct
                            ? 'var(--liwaza-success)'
                            : 'var(--liwaza-error)',
                        margin: 0,
                      }}
                    >
                      {practiceAnswers[question.id] === question.correct
                        ? 'Correct!'
                        : 'Incorrect'}
                    </h5>
                  </div>

                  <p style={{ marginBottom: '0.5rem' }}>
                    <strong>Correct Answer:</strong>{' '}
                    {String.fromCharCode(65 + question.correct)}.{' '}
                    {question.options[question.correct]}
                  </p>

                  <p style={{ marginBottom: '1rem' }}>
                    <strong>Explanation:</strong> {question.explanation}
                  </p>

                  <div
                    style={{
                      background: 'var(--liwaza-tertiary-container)',
                      color: 'var(--liwaza-on-tertiary-container)',
                      padding: '0.75rem',
                      borderRadius: '6px',
                      border: '1px solid var(--liwaza-tertiary)',
                    }}
                  >
                    <strong>Question Type Analysis:</strong>{' '}
                    {question.questionTypeExplanation}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Quick Reference Guide */}
      <div className="lesson-intro">
        <h4
          style={{
            color: 'var(--liwaza-on-primary-container)',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <FiBookOpen />
          Quick Reference: Approaching Each Question Type
        </h4>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1rem',
            marginTop: '1rem',
          }}
        >
          <div
            style={{
              background: 'var(--liwaza-surface-container)',
              padding: '1rem',
              borderRadius: '8px',
              border: '1px solid var(--liwaza-outline-variant)',
            }}
          >
            <h5
              style={{ color: 'var(--liwaza-primary)', marginBottom: '0.5rem' }}
            >
              Main Idea & Purpose
            </h5>
            <p
              style={{
                fontSize: '0.9rem',
                margin: 0,
                color: 'var(--liwaza-on-surface)',
              }}
            >
              Read the first and last paragraphs carefully. Look for thesis
              statements and conclusions.
            </p>
          </div>

          <div
            style={{
              background: 'var(--liwaza-surface-container)',
              padding: '1rem',
              borderRadius: '8px',
              border: '1px solid var(--liwaza-outline-variant)',
            }}
          >
            <h5
              style={{ color: 'var(--liwaza-primary)', marginBottom: '0.5rem' }}
            >
              Detail Questions
            </h5>
            <p
              style={{
                fontSize: '0.9rem',
                margin: 0,
                color: 'var(--liwaza-on-surface)',
              }}
            >
              Return to the passage. Find exact textual support. Avoid
              inference.
            </p>
          </div>

          <div
            style={{
              background: 'var(--liwaza-surface-container)',
              padding: '1rem',
              borderRadius: '8px',
              border: '1px solid var(--liwaza-outline-variant)',
            }}
          >
            <h5
              style={{ color: 'var(--liwaza-primary)', marginBottom: '0.5rem' }}
            >
              Inference Questions
            </h5>
            <p
              style={{
                fontSize: '0.9rem',
                margin: 0,
                color: 'var(--liwaza-on-surface)',
              }}
            >
              Stay close to the passage. Look for logical connections between
              stated facts.
            </p>
          </div>

          <div
            style={{
              background: 'var(--liwaza-surface-container)',
              padding: '1rem',
              borderRadius: '8px',
              border: '1px solid var(--liwaza-outline-variant)',
            }}
          >
            <h5
              style={{ color: 'var(--liwaza-primary)', marginBottom: '0.5rem' }}
            >
              Tone & Function
            </h5>
            <p
              style={{
                fontSize: '0.9rem',
                margin: 0,
                color: 'var(--liwaza-on-surface)',
              }}
            >
              Pay attention to word choice, context, and how parts relate to the
              whole argument.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="lesson-container">
      {/* Header */}
      <div className="lesson-header">
        <button
          onClick={() =>
            navigate('/exams/gre/', { state: { activeCategory: 'reading' } })
          }
          className="back-button"
        >
          <FiArrowLeft /> Back to Reading Comprehension
        </button>

        <div className="header-actions">
          <button onClick={scrollToPractice} className="practice-button">
            Practice
          </button>
        </div>
      </div>

      {/* Title */}
      <div className="lesson-title-section">
        <h1 className="lesson-title">
          <FiEye />
          Lesson 1: 8 Types of RC Questions
        </h1>
        <p className="lesson-subtitle">
          Master question recognition to apply the right strategy every time.
          Learn the distinct patterns, keywords, and approaches for each of the
          eight question types you'll encounter on the GRE.
        </p>
      </div>

      {/* Content */}
      {renderOverview()}
      {renderPractice()}

      {/* Get Started With Component */}
      <div style={{ margin: '3rem 0 2rem 0' }}>
        <GetStartedWith
          title="Need help with Question Types recognition?"
          spaceName="Verbal vault"
          humanCoachTitle="Talk to a GRE coach"
          liwazaAITitle="Ask AI"
          communityTitle="Ask other students"
          showHumanCoach={true}
          showLiwazaAI={true}
          showCommunity={true}
          aifirstmessage="I need help understanding the 8 Types of Reading Comprehension Questions on the GRE"
          aicontext="Every GRE Reading Comprehension question falls into one of eight distinct categories. Learning to quickly identify question types will help you apply the right strategy and avoid common traps. Each type requires a specific approach and has predictable patterns."
        />
      </div>

      {/* Navigation */}
      <div className="lesson-navigation">
        <div
          style={{
            background: 'var(--liwaza-surface-container)',
            color: 'var(--liwaza-on-surface)',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            border: '1px solid var(--liwaza-outline-variant)',
          }}
        >
          This is the first lesson
        </div>

        <button
          onClick={() =>
            navigate(
              '/exams/gre/reading-comprehension/lesson/five-step-process'
            )
          }
          className="next-lesson-button"
        >
          Next: 5-Step Process <FiArrowRight />
        </button>
      </div>
    </div>
  );
}
